import * as React from 'react';
import { useSelector } from 'react-redux';
import { DependenciesReleaseReport } from '@/types/dependencies';
import { RootState } from '@/reducers/reducers';
import { getSeenDependenciesUpdates, setSeenDependenciesUpdates } from '@/helpers/localStorageHelper';
import { useGetDependenciesQuery } from '@/middlewares/defaultApi';
import {
    selectAppSettings,
    selectAuthCredentials,
    selectIsReleaseCompleted,
    selectIsReleaseKickedOff,
    selectWorkflowId,
    selectWorkflowRunId,
} from '@/selectors/selectors';
import * as androidUtils from '@/components/dependencies/androidDependenciesUtils';
import * as iosUtils from '@/components/dependencies/iosDependenciesUtils';

// An update is identified by the version it offers, so a newer release of an already outdated dependency counts as new.
const getUpdateKeys = (reports: DependenciesReleaseReport[]): string[] =>
    reports.reduce<string[]>((keys, report) => {
        const updates: { identity: string; latestVersion?: string }[] =
            report.platform === 'android' ?
                androidUtils.getAvailableUpdates(report.androidReport)
            :   iosUtils.getAvailableUpdates(report.iosReport);
        return keys.concat(updates.map((dep) => `${report.destinationId}:${dep.identity}@${dep.latestVersion ?? ''}`));
    }, []);

const parseSeenUpdates = (seenUpdates: string): string[] => {
    try {
        const parsed: unknown = JSON.parse(seenUpdates);
        return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
    } catch {
        return [];
    }
};

interface UseDependenciesNewUpdatesProps {
    orgId: string;
    appId: string;
    releaseId: string;
    isViewingDependencies: boolean;
    skip?: boolean;
}

export const useDependenciesNewUpdates = ({
    orgId,
    appId,
    releaseId,
    isViewingDependencies,
    skip = false,
}: UseDependenciesNewUpdatesProps): number => {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));
    const hasConfigs = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, appId);
        return (
            Object.keys(appSettings?.iosDependenciesConfigs ?? {}).length > 0 ||
            Object.keys(appSettings?.androidDependenciesConfigs ?? {}).length > 0
        );
    });
    // Reports are only refreshed while the release branch is live, and nothing can be updated outside of it.
    const isReleaseCompleted = useSelector((state: RootState) => selectIsReleaseCompleted(state, releaseId));
    const isReleaseKickedOff = useSelector((state: RootState) => selectIsReleaseKickedOff(state, releaseId));

    // Same args as the Dependencies page so both share one cache entry, which websocket updates keep current.
    const { currentData } = useGetDependenciesQuery(
        { orgId, workflowId: workflowId ?? '', workflowRunId },
        {
            skip:
                skip ||
                !auth ||
                !workflowId ||
                !workflowRunId ||
                !hasConfigs ||
                isReleaseCompleted ||
                !isReleaseKickedOff,
        },
    );
    const updateKeys = React.useMemo(() => getUpdateKeys(currentData?.data?.reports ?? []), [currentData]);

    // Updates are keyed by app rather than release: a release branch carries over the previous one's outdated
    // dependencies, and those were already seen there.
    const [seenUpdates, setSeenUpdates] = React.useState(() => getSeenDependenciesUpdates(appId));
    React.useEffect(() => {
        setSeenUpdates(getSeenDependenciesUpdates(appId));
    }, [appId]);

    React.useEffect(() => {
        if (!currentData) {
            return;
        }
        const storedSeenUpdates = getSeenDependenciesUpdates(appId);
        if (isViewingDependencies || !storedSeenUpdates) {
            const serialized = JSON.stringify(
                Array.from(new Set([...parseSeenUpdates(storedSeenUpdates), ...updateKeys])),
            );
            setSeenDependenciesUpdates(appId, serialized);
            setSeenUpdates(serialized);
        }
    }, [currentData, updateKeys, isViewingDependencies, appId]);

    const seenUpdateKeys = React.useMemo(() => new Set(parseSeenUpdates(seenUpdates)), [seenUpdates]);
    if (isViewingDependencies) {
        return 0;
    }
    return updateKeys.filter((key) => !seenUpdateKeys.has(key)).length;
};
